import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '@/state/language/LanguageProvider';
import {
  AssessmentPreviewFlow,
  type AssessmentPreviewTarget,
} from './AssessmentPreviewFlow';

export interface AssessmentPreviewModalProps {
  target: AssessmentPreviewTarget;
  onClose: () => void;
}

/** Full-screen overlay for assessment preview — portals above TMOS app chrome. */
export const AssessmentPreviewModal: React.FC<AssessmentPreviewModalProps> = ({ target, onClose }) => {
  const { t } = useLanguage();
  const [skipRequired, setSkipRequired] = useState(true);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  const flowKey = target.kind === 'template' ? `t-${target.templateId}` : `c-${target.campaignId}`;

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex flex-col bg-[var(--color-secondary-bg)] text-[var(--color-surface-text)]"
      role="dialog"
      aria-modal="true"
      aria-label={t('assessmentEngine.preview.button')}
    >
      <div className="flex shrink-0 items-center justify-between border-b border-[var(--color-border)] bg-[var(--color-surface-bg)] px-4 py-2">
        <p className="text-sm font-medium">{t('assessmentEngine.preview.button')}</p>
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded px-2 py-1 text-sm text-[var(--color-surface-text)] hover:bg-[var(--color-hover-bg)]"
          onClick={onClose}
        >
          <XMarkIcon className="h-5 w-5" aria-hidden />
          {t('assessmentEngine.preview.close')}
        </button>
      </div>

      <AssessmentPreviewFlow
        key={flowKey}
        target={target}
        onClose={onClose}
        skipRequired={skipRequired}
        onSkipRequiredChange={setSkipRequired}
      />
    </div>,
    document.body
  );
};
